/**
 * WORKING IT. Take it, move it, say what happens next, or lose it: the one form a card and the
 * panel both draw, each copy with field ids of its own.
 *
 * AND, WHILE IT IS QUOTED, WHAT HELD IT BACK (ops #86): one of the business's seven words and their
 * own words beside it, typed as the office learns it, usually on the day-one call. Never a price: the
 * server refuses a figure in the words, and the box says so in coral as it is typed.
 */
import { useState } from 'react';
import {
  HELD_BACK_LABELS,
  HELD_BACK_REASONS,
  TYPED_CHANNELS,
  type HeldBackReason,
} from '@fk/shared';
import {
  ApiError,
  patchEnquiry,
  type EnquiryPatch,
  type EnquiryRow,
  type Me,
  type OfficeUser,
} from '../../api.ts';
import { Button, SelectField, TextAreaField, TextField } from '../../components/Field.tsx';
import { ErrorNotice } from '../../components/Notice.tsx';
import { SourceFields } from '../../components/SourceFields.tsx';
import { WordsCheck } from '../../components/WordsCheck.tsx';
import { isDay, orNull } from '../../format.ts';
import { ENQUIRY_STATE_LABELS } from '../../labels.ts';
import {
  asksHowSheHeard,
  firstAnswerPatch,
  sourceAnswerOf,
  type SourceAnswer,
} from '../../source.ts';
import { targetsFrom, type Movable } from './rows.ts';

export function WorkItForm({
  idPrefix,
  row,
  me,
  people,
  onChanged,
}: {
  /** Where this copy is drawn, because the card and the panel are both in the document. */
  idPrefix: string;
  row: EnquiryRow;
  me: Me;
  people: readonly OfficeUser[];
  onChanged: (sentence: string) => void;
}) {
  const targets = targetsFrom(row.state);
  // THE QUESTION, asked once and only where nobody has asked it. Every call opens an enquiry filed
  // `phone` with a literal under it, and the answer the office actually collects had nowhere to
  // land: a wrong channel stayed wrong through the conversion and into the household, and every
  // kill rule counted it. It rides in whatever patch this form is already sending.
  //
  // It is NOT gated on the move that stamps the first reply, which reads right and is exactly
  // wrong: `facts.ts` moves an ANSWERED call to `contacted` and stamps `first_response_at` in the
  // same batch, so the one enquiry where somebody had her on the line arrives here already past
  // that gate. `asksHowSheHeard` reads the row instead, and answering it is what ends it.
  const asking = asksHowSheHeard(row);
  const current = sourceAnswerOf(row);
  const [state, setState] = useState<Movable | ''>('');
  // Empty rather than prefilled, because "Not asked" is the choice sitting under the finger and
  // the only detail on a row this is drawn on is the machine's own literal. `firstAnswerPatch`
  // is what makes leaving it alone cost nothing.
  const [heard, setHeard] = useState<SourceAnswer>({
    sourceChannel: '',
    sourceDetail: '',
    referredByClientId: row.referredByClientId,
  });
  // Seeded from the row, so the picker opens on whoever has it and choosing nothing changes
  // nothing. The empty string is a real answer: it takes the enquiry off whoever holds it.
  const [giveTo, setGiveTo] = useState(row.ownerUserId ?? '');
  const [nextAction, setNextAction] = useState(row.nextAction ?? '');
  const [nextActionOn, setNextActionOn] = useState(row.nextActionOn ?? '');
  const [notes, setNotes] = useState(row.notes ?? '');
  const [heldBack, setHeldBack] = useState<HeldBackReason | ''>(row.heldBack ?? '');
  const [heldBackNote, setHeldBackNote] = useState(row.heldBackNote ?? '');
  const quoted = row.state === 'quoted';
  const [lostReason, setLostReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const field = (name: string) => `${idPrefix}-${name}-${row.id}`;

  /** On a refusal the server's sentence is shown and nothing typed is touched. */
  async function run(work: () => Promise<unknown>, sentence: string) {
    setBusy(true);
    setProblem(null);
    try {
      await work();
      onChanged(sentence);
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not recorded.');
    } finally {
      setBusy(false);
    }
  }

  /** Owning it means it is yours to follow up, and it moves from whoever has it now. */
  function take() {
    setGiveTo(me.userId);
    void run(() => patchEnquiry(row.id, { ownerUserId: me.userId }), 'It is yours.');
  }

  /**
   * GIVING IT TO SOMEBODY ELSE, which "Take it" alone could never do.
   *
   * `GET /api/users` answers with everybody who has a sign-in and `officePeople` narrows it where
   * this screen reads it, so this list holds nobody an enquiry should not reach, and the server
   * refuses a cleaner and a suspended account again on the way in. The empty
   * choice takes it off whoever has it, which is what somebody does before she goes away for a
   * week: an enquiry belonging to a person who is not there is worse than one belonging to nobody,
   * because the coral pill that says nobody owns it is how the office finds it again.
   */
  function give() {
    const chosen = giveTo === '' ? null : giveTo;
    void run(
      () => patchEnquiry(row.id, { ownerUserId: chosen }),
      chosen === null
        ? 'Taken off, and back on the list with nobody owning it.'
        : chosen === me.userId
          ? 'It is yours.'
          : `Given to ${people.find((person) => person.id === chosen)?.displayName ?? 'them'}.`,
    );
  }

  /** One patch carrying only what changed, so an untouched box never overwrites the row. */
  function save() {
    // What she said when she was asked, in the same write as whatever else this form is saving.
    // Left on "Not asked" it contributes nothing at all, which is `firstAnswerPatch`'s whole job:
    // an empty box read as a cleared column would erase the `inbound call` the phone seam wrote.
    const heardPatch = asking ? firstAnswerPatch(heard, current) : {};
    const patch: EnquiryPatch = {
      ...(state ? { state } : {}),
      ...(nextAction.trim() !== (row.nextAction ?? '') ? { nextAction: orNull(nextAction) } : {}),
      ...(nextActionOn !== (row.nextActionOn ?? '') ? { nextActionOn: orNull(nextActionOn) } : {}),
      ...(notes.trim() !== (row.notes ?? '') ? { notes: orNull(notes) } : {}),
      // What held it back, only while it is quoted, and only what changed.
      ...(quoted && heldBack !== (row.heldBack ?? '')
        ? { heldBack: heldBack === '' ? null : heldBack }
        : {}),
      ...(quoted && heldBackNote.trim() !== (row.heldBackNote ?? '')
        ? { heldBackNote: orNull(heldBackNote) }
        : {}),
      ...heardPatch,
    };
    if (Object.keys(patch).length === 0) {
      setProblem('Nothing here has changed yet.');
      return;
    }
    if (patch.nextActionOn && !isDay(patch.nextActionOn)) {
      setProblem('The day for the next action is not a real date.');
      return;
    }
    // What the sentence says happened, and the two halves are independent now: the answer no
    // longer rides on the move, so a save can carry either, both or neither.
    const said = heardPatch.sourceChannel !== undefined || heardPatch.sourceDetail !== undefined;
    const firstReply = state !== '' && row.state === 'new' && row.firstResponseAt === null;
    void run(
      () => patchEnquiry(row.id, patch),
      said && firstReply
        ? 'Saved, with the first reply and what they said about how they heard of us.'
        : said
          ? 'Saved, with what they said about how they heard of us.'
          : firstReply
            ? 'Saved, and the moment of the first reply with it.'
            : 'Saved.',
    );
  }

  /**
   * The reason rides along only when typed. The server refuses lost with no reason on the row and
   * says so; an empty box sent as null would instead blank a reason from an earlier time it was
   * lost, which is a fact worth keeping.
   */
  function markLost() {
    const reason = orNull(lostReason);
    void run(
      () => patchEnquiry(row.id, { state: 'lost', ...(reason ? { lostReason: reason } : {}) }),
      'Marked lost.',
    );
  }

  return (
    <div>
      {problem ? (
        <div className="mt-2 mb-3">
          <ErrorNotice title="Not recorded" message={problem} />
        </div>
      ) : null}

      <div className="mt-3">
        {row.ownerUserId !== me.userId ? (
          <>
            <Button tone="quiet" onClick={take} disabled={busy}>
              Take it
            </Button>
            <p className="mt-1 text-xs text-navy/65">
              Owning it means it is yours to follow up.
              {row.ownerUserId ? ' It moves from whoever has it now.' : ''}
            </p>
          </>
        ) : null}

        {/*
         * AND THE OTHER ANSWER, which "Take it" on its own could never give: hand it to somebody
         * else. The names come from `GET /api/users`, which answers with everybody who has a
         * sign-in, narrowed to the office by `officePeople` where the screen reads it, so a
         * cleaner and a suspended account are both off this list and the door refuses either one
         * again anyway. Drawn only where those names arrived: a picker with nothing in it is worse
         * than the button above, which still works.
         */}
        {people.length > 0 ? (
          <div className="mt-3 md:grid md:grid-cols-[minmax(0,1fr)_auto] md:items-start md:gap-3">
            <SelectField
              id={field('owner')}
              label="Give it to"
              value={giveTo}
              onChange={setGiveTo}
              options={people.map((person) => person.id)}
              toLabel={(chosen) =>
                chosen === me.userId
                  ? 'You'
                  : (people.find((person) => person.id === chosen)?.displayName ?? chosen)
              }
              placeholder="Nobody"
            />
            <div className="mb-4 md:mt-6">
              <Button
                tone="quiet"
                onClick={give}
                disabled={busy || giveTo === (row.ownerUserId ?? '')}
                full
              >
                Give it
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="mt-4 border-t border-navy/10 pt-3">
        <div className="md:grid md:grid-cols-2 md:gap-3">
          {targets.length > 0 ? (
            <SelectField
              id={field('state')}
              label="Move it to"
              value={state}
              onChange={setState}
              options={targets}
              toLabel={(option) => ENQUIRY_STATE_LABELS[option]}
              placeholder="Leave it as it is"
              hint={
                row.state === 'new'
                  ? 'Leaving new stamps this moment as the first reply, which is the clock the same-day promise reads.'
                  : undefined
              }
            />
          ) : null}
          <TextField
            id={field('next-on')}
            label="By which day"
            type="date"
            value={nextActionOn}
            onChange={setNextActionOn}
          />
        </div>

        {asking ? (
          <div className="mb-4 rounded-lg border border-navy/15 bg-navy/5 p-3">
            <h4 className="font-serif text-base leading-tight">How did they hear about us?</h4>
            <p className="mt-1 mb-3 text-xs text-navy/70">
              The business publishes one number, so nothing about a call says what produced it and
              their answer is the whole record. Asked while they are on the line, saved with
              everything else here, and asked once: a word chosen here ends the question.
            </p>
            <SourceFields
              idPrefix={field('heard')}
              channels={TYPED_CHANNELS}
              value={heard}
              onChange={setHeard}
              channelLabel="What they said"
              channelPlaceholder="Not asked, leave it as a phone call"
              channelHint="Never guessed. Nothing is written here unless they said it."
              detailHint="Which Nextdoor thread, which realtor's office, who told them about us."
            />
          </div>
        ) : null}

        {quoted ? (
          <div className="mb-4 rounded-lg border border-navy/15 bg-navy/5 p-3">
            <h4 className="font-serif text-base leading-tight">What held them back</h4>
            <p className="mt-1 mb-3 text-xs text-navy/70">
              Typed as you learn it, usually on the day-one call. Never a price: the price is the
              published one, and the follow-up emails point there.
            </p>
            <SelectField
              id={field('held-back')}
              label="Held back by"
              value={heldBack}
              onChange={setHeldBack}
              options={HELD_BACK_REASONS}
              toLabel={(reason) => HELD_BACK_LABELS[reason]}
              placeholder="Not said yet"
            />
            <TextAreaField
              id={field('held-back-note')}
              label="In their words"
              value={heldBackNote}
              onChange={setHeldBackNote}
            />
            <WordsCheck words={heldBackNote} />
          </div>
        ) : null}

        <TextField
          id={field('next')}
          label="Next action"
          value={nextAction}
          onChange={setNextAction}
          hint="Call them back, send the quote, walk through on Tuesday."
          autoComplete="off"
        />
        <TextAreaField id={field('notes')} label="Notes" value={notes} onChange={setNotes} />
        <Button onClick={save} disabled={busy} full>
          {busy ? 'Recording' : 'Save'}
        </Button>
      </div>

      {row.state !== 'lost' ? (
        <div className="mt-4 border-t border-navy/10 pt-3">
          <TextField
            id={field('lost')}
            label="Why it was lost"
            value={lostReason}
            onChange={setLostReason}
            hint="In their words. Went with somebody else, out of area, never answered."
            autoComplete="off"
          />
          <Button tone="quiet" onClick={markLost} disabled={busy}>
            Mark it lost
          </Button>
        </div>
      ) : null}
    </div>
  );
}
